import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { scrollToSection } from "./scroll";

const Chevron = () => (
  <svg className="bp-chevron" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
    <path d="M1 3.5 5 7l4-3.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

export const NavDropdown = ({ group }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const hoverRef = useRef(false);
  const menuId = `bp-menu-${group.id}`;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDocClick);
    return () => document.removeEventListener("pointerdown", onDocClick);
  }, [open]);

  const focusItem = (index) => {
    const items = wrapRef.current?.querySelectorAll("[role='menuitem']");
    if (!items?.length) return;
    items[(index + items.length) % items.length].focus();
  };

  const onTriggerKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => focusItem(0));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const onMenuKeyDown = (e) => {
    const items = Array.from(wrapRef.current.querySelectorAll("[role='menuitem']"));
    const current = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown") { e.preventDefault(); focusItem(current + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusItem(current - 1); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
    else if (e.key === "Tab") setOpen(false);
  };

  const onPointerEnter = (e) => { if (e.pointerType === "mouse") { hoverRef.current = true; setOpen(true); } };
  const onPointerLeave = (e) => { if (e.pointerType === "mouse") { hoverRef.current = false; setOpen(false); } };
  const onTriggerClick = () => {
    if (hoverRef.current) { hoverRef.current = false; setOpen(true); return; }
    setOpen((v) => !v);
  };

  const go = (target) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(target, navigate);
  };

  return (
    <li
      ref={wrapRef}
      className={`bp-nav-item bp-nav-item--dropdown${open ? " is-open" : ""}`}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <button
        ref={triggerRef}
        type="button"
        className="bp-nav-link bp-nav-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={onTriggerClick}
        onKeyDown={onTriggerKeyDown}
        data-testid={`nav-${group.id}-trigger`}
      >
        {group.label}
        <Chevron />
      </button>
      <ul id={menuId} className="bp-dropdown" role="menu" aria-label={group.label} onKeyDown={onMenuKeyDown} hidden={!open}>
        {group.items.map((item) => (
          <li key={item.target + item.label} role="none">
            <a
              role="menuitem"
              href={`#${item.target}`}
              className="bp-dropdown-link"
              onClick={go(item.target)}
              tabIndex={open ? 0 : -1}
              data-testid={`nav-${group.id}-${item.target}`}
            >
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </li>
  );
};
